import styled from '@emotion/styled/macro';
import { forwardRef, useMemo, useRef } from 'react';
import { getEnvironment } from '@sunrun/sales-experience-shared';
import {
  EVENT_MOBILE_APP_EXTERNAL_LINK_CLICKED,
  EVENT_SPLAT_IN_MOBILE_APP,
  publishMessage,
  QUERY_SPLAT_IN_MOBILE_APP,
  useSubscribe,
} from 'helpers/messaging';
import type { Message } from 'helpers/messaging';

type IFrameProps = {
  basePath: string;
  src: string;
  title: string;
  visible: boolean;
};

const IFrame = forwardRef<HTMLIFrameElement, Omit<IFrameProps, 'ref'>>(
  ({ basePath, src, title, visible }, ref) => {
    return (
      <IFrameContainer
        style={{ display: visible ? 'block' : 'none' }}
        key={basePath}
        ref={ref}
        src={src}
        title={title}
        allow="geolocation; fullscreen; clipboard-write;"
      />
    );
  },
);

const IFrameContainer = styled.iframe`
  width: 100%;
  height: 100vh;
  border: 0px;
  flex-grow: 1;
  display: block;
`;

const env = getEnvironment();
const config = {
  origin: {
    development: 'https://devmaj-go.sunrun.com/one',
    relcert: 'https://relcert-go.sunrun.com/one',
    staging: 'https://majstg-go.sunrun.com/one',
    production: 'https://go.sunrun.com/one',
  },
  pages: {
    customers: {
      title: 'Customers',
      iFrameUrl: '/customers/list?fixedTab=true',
    },
  },
};

type Props = {
  page: 'customers';
  visible: boolean;
};
const SplatIFrame = ({ page, visible }: Props) => {
  const ref = useRef<HTMLIFrameElement | null>(null);

  const { iFrameUrl, title } = useMemo(() => {
    const origin = config.origin[env];
    const pageConfig = config.pages[page];
    const iFrameUrl = pageConfig.iFrameUrl;
    return { iFrameUrl: origin + iFrameUrl, title: pageConfig.title };
  }, [page]);

  useSubscribe((message: Message) => {
    const handleMobileMessage = (mobileMessage: Message) => {
      const { type } = mobileMessage;
      const inMobileApp = 'ReactNativeWebView' in window;
      if (type === QUERY_SPLAT_IN_MOBILE_APP) {
        const iFrame = ref.current?.contentWindow;
        if (!iFrame) {
          return;
        }

        const response: Message = {
          source: 'Dashboard',
          type: EVENT_SPLAT_IN_MOBILE_APP,
          payload: {
            isInOneMobileApp: inMobileApp,
          },
        };
        iFrame?.postMessage(response, { targetOrigin: '*' });
      } else if (type === EVENT_MOBILE_APP_EXTERNAL_LINK_CLICKED) {
        if (inMobileApp) {
          // @ts-ignore
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({
              type: 'EXTERNAL_LINK_CLICKED',
              payload: {
                url: mobileMessage.payload.url,
              },
            }),
          );
        }
      }
    };

    switch (message.source) {
      // Replicate messages "upward" to container
      case 'Legacy Splat':
        handleMobileMessage(message);
        publishMessage({ ...message, source: 'Dashboard' });
        break;
      // Replicate messages "downward" to SPLAT
      case 'Auth':
      case 'Container':
        if (ref.current) {
          ref.current.contentWindow?.postMessage(
            { ...message, source: 'Dashboard' },
            { targetOrigin: '*' },
          );
        }
        break;
      default:
        return message;
    }
  });

  return (
    <IFrame
      visible={visible}
      basePath={iFrameUrl}
      ref={ref}
      title={title}
      src={iFrameUrl}></IFrame>
  );
};

export { SplatIFrame };
